import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import {
  FileText,
  Sparkles,
  Download,
  Share2,
  Copy,
  Check,
  RefreshCw,
  ArrowLeft,
  ExternalLink,
  Send,
  Mail,
  FileSpreadsheet,
  FileDown,
  HelpCircle,
  X,
  Save,
  Trash2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import EmailComposer from "./EmailComposer";
import { useAutosave } from "@/hooks/useAutosave";

// CSV Import Help Modal Component
function CSVImportGuide({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-700">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-indigo-400" />
            <h3 className="text-lg font-semibold text-indigo-500">Import CSV to Accounting Software</h3>
          </div>
          <button onClick={onClose} className="p-2 text-indigo-400 hover:text-indigo-500 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* QuickBooks */}
          <div className="space-y-3">
            <h4 className="text-indigo-500 font-medium flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center text-green-400 font-bold text-sm">QB</span>
              QuickBooks Online
            </h4>
            <ol className="text-indigo-400 text-sm space-y-2 list-decimal list-inside">
              <li>Go to <span className="text-indigo-500">Settings (⚙️) → Import Data</span></li>
              <li>Select <span className="text-indigo-500">"Invoices"</span> or <span className="text-indigo-500">"Products and Services"</span></li>
              <li>Click <span className="text-indigo-500">"Browse"</span> and select your CSV file</li>
              <li>Map the columns: Item → Product/Service, Rate → Rate, Amount → Amount</li>
              <li>Click <span className="text-indigo-500">"Import"</span> to complete</li>
            </ol>
            <a 
              href="https://quickbooks.intuit.com/learn-support/en-us/import-or-export-data/how-to-import-data-into-quickbooks-online/00/186411" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-indigo-400 hover:text-slate-400 flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> QuickBooks Import Guide
            </a>
          </div>

          {/* Xero */}
          <div className="space-y-3">
            <h4 className="text-indigo-500 font-medium flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center text-indigo-400 font-bold text-sm">X</span>
              Xero
            </h4>
            <ol className="text-indigo-400 text-sm space-y-2 list-decimal list-inside">
              <li>Go to <span className="text-indigo-500">Business → Invoices</span></li>
              <li>Click <span className="text-indigo-500">"Import"</span> at the top right</li>
              <li>Select your CSV file</li>
              <li>Match columns to Xero fields (Description, Quantity, Unit Price)</li>
              <li>Review and click <span className="text-indigo-500">"Complete Import"</span></li>
            </ol>
            <a 
              href="https://central.xero.com/s/article/Import-invoices" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-indigo-400 hover:text-slate-400 flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Xero Import Guide
            </a>
          </div>

          {/* Wave */}
          <div className="space-y-3">
            <h4 className="text-indigo-500 font-medium flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-bold text-sm">W</span>
              Wave
            </h4>
            <ol className="text-indigo-400 text-sm space-y-2 list-decimal list-inside">
              <li>Go to <span className="text-indigo-500">Sales → Products & Services</span></li>
              <li>Click <span className="text-indigo-500">"Import"</span></li>
              <li>Upload your CSV file</li>
              <li>Map columns and verify the preview</li>
              <li>Click <span className="text-indigo-500">"Import"</span></li>
            </ol>
            <a 
              href="https://support.waveapps.com/hc/en-us/articles/360017605571-Import-Products-and-Services" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-indigo-400 hover:text-slate-400 flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Wave Import Guide
            </a>
          </div>

          {/* CSV Format Info */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <h4 className="text-indigo-500 font-medium mb-2">CSV Format</h4>
            <p className="text-indigo-400 text-sm mb-2">Your exported CSV contains these columns:</p>
            <code className="text-xs text-indigo-400 bg-slate-900 px-2 py-1 rounded">
              Item, Description, Quantity, Rate, Amount
            </code>
          </div>
        </div>
        
        <div className="p-4 border-t border-slate-700">
          <button
            onClick={onClose}
            className="w-full py-2 bg-slate-800 hover:bg-slate-400 text-indigo-500 rounded-lg transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProposalEditor() {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [project, setProject] = useState(null);
  const [clients, setClients] = useState([]);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showCSVGuide, setShowCSVGuide] = useState(false);
  
  // Auto-save for proposal content
  const draftKey = `proposal_${projectId}`;
  const [mdBody, setMdBody, clearDraft, hasDraft] = useAutosave(draftKey, "", 1500);
  const [autoSaved, setAutoSaved] = useState(false);
  
  // Show auto-saved indicator when content changes
  useEffect(() => {
    if (mdBody && hasDraft) {
      setAutoSaved(true);
      const timer = setTimeout(() => setAutoSaved(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mdBody, hasDraft]);

  useEffect(() => {
    fetchProject();
    fetchProposals();
    fetchClients();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const res = await api.get(`/projects/${projectId}`);
      setProject(res.data);
    } catch (e) {
      console.error("Error fetching project:", e);
    }
  };

  const fetchProposals = async () => {
    try {
      const res = await api.get(`/proposals/projects/${projectId}`);
      setProposals(res.data.proposals || []);
      if (res.data.proposals?.length > 0) {
        const latest = res.data.proposals[0];
        setSelectedProposal(latest);
        // Only override draft if proposal content is newer
        if (!hasDraft || latest.md_body) {
          setMdBody(latest.md_body || "");
        }
      }
    } catch (e) {
      console.error("Error fetching proposals:", e);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await api.get("/clients");
      setClients(res.data);
    } catch (e) {
      console.error("Error fetching clients:", e);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/proposals/generate", {
        project_id: projectId,
        include: {
          summary: true,
          deliverables: true,
          timeline: true,
          pricing: true,
          terms: true,
          revision_policy: true
        }
      });
      
      setMdBody(res.data.md_body || "");
      setSelectedProposal({ id: res.data.proposal_id, version: res.data.version });
      clearDraft(); // Clear draft after successful generation
      toast.success("Proposal generated successfully!");
      fetchProposals();
    } catch (e) {
      console.error("Generate error:", e);
      toast.error(e.response?.data?.detail || "Failed to generate proposal");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    if (!selectedProposal?.id) return;
    
    try {
      const res = await api.post(`/proposals/${selectedProposal.id}/export`, { format });
      
      if (format === "md" || format === "html") {
        // Download as file
        const blob = new Blob([res.data.body], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.data.filename;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported as ${format.toUpperCase()}`);
      }
    } catch (e) {
      console.error("Export error:", e);
      toast.error("Failed to export proposal");
    }
  };

  const handleExportCSV = async () => {
    if (!projectId) return;
    
    try {
      const response = await api.get(`/accounting/projects/${projectId}/export-csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clientkit_invoice_${projectId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      
      toast.success("CSV exported! Import into QuickBooks, Xero, or Wave.");
    } catch (e) {
      console.error("CSV export error:", e);
      const detail = e.response?.data?.detail;
      if (detail?.code === 'FEATURE_GATED') {
        toast.error(`Upgrade required: CSV export is a Pro feature`);
      } else if (e.response?.status === 404) {
        toast.error("No proposal found. Generate a proposal first.");
      } else {
        toast.error("Failed to export CSV");
      }
    }
  };

  const handleExportPDF = async () => {
    if (!selectedProposal?.id) return;
    
    try {
      const response = await api.get(`/proposals/${selectedProposal.id}/export-pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ClientKit_Proposal_${selectedProposal.title || 'document'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      
      toast.success("PDF exported successfully!");
    } catch (e) {
      console.error("PDF export error:", e);
      toast.error("Failed to export PDF");
    }
  };

  const handleShare = async () => {
    if (!selectedProposal?.id) return;
    
    try {
      const res = await api.post(`/proposals/${selectedProposal.id}/share`, {
        allowed_sections: ["proposal"],
        expires_days: 30
      });
      
      const fullUrl = `${window.location.origin}${res.data.url}`;
      setShareUrl(fullUrl);
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Share link copied to clipboard!");
    } catch (e) {
      console.error("Share error:", e);
      toast.error("Failed to create share link");
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(mdBody);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const getProposalEmailContent = () => {
    const proposalTitle = selectedProposal?.title || project?.title || "Proposal";
    const subject = `Subject: ${proposalTitle}`;
    
    // Create a simplified email body from the proposal
    const emailBody = `Dear Client,

I'm pleased to share the proposal for ${project?.title || "our project"}.

${shareUrl ? `You can view the full proposal here: ${shareUrl}` : "Please find the proposal details below:"}

---

${mdBody}

---

Please let me know if you have any questions or would like to discuss any aspects of this proposal.

Best regards,
[Your Name]`;
    
    return `${subject}\n\n${emailBody}`;
  };

  return (
    <div className="space-y-6" data-testid="proposal-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/projects"
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-indigo-400" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-indigo-500">Proposal Builder</h1>
            <p className="text-indigo-400">
              {project?.title || "Project"} — Create professional proposals
            </p>
          </div>
        </div>
        <Badge className="bg-indigo-500/20 text-indigo-400">
          {proposals.length} proposal{proposals.length !== 1 ? "s" : ""}
        </Badge>
      </div>

      {/* Action Bar */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-slate-600 text-white px-4 py-2 rounded-xl transition-colors"
              data-testid="generate-proposal-btn"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              Generate Proposal
            </button>
            
            <button
              onClick={() => handleExport("md")}
              disabled={!selectedProposal}
              className="flex items-center gap-2 bg-slate-400 hover:bg-slate-600 disabled:opacity-50 text-indigo-500 px-4 py-2 rounded-xl transition-colors"
            >
              <Download className="w-4 h-4" />
              Export MD
            </button>
            
            <button
              onClick={() => handleExport("html")}
              disabled={!selectedProposal}
              className="flex items-center gap-2 bg-slate-400 hover:bg-slate-600 disabled:opacity-50 text-indigo-500 px-4 py-2 rounded-xl transition-colors"
            >
              <FileText className="w-4 h-4" />
              Export HTML
            </button>
            
            <button
              onClick={handleExportPDF}
              disabled={!selectedProposal}
              className="flex items-center gap-2 bg-red-500/20 hover:bg-red-500/30 disabled:opacity-50 text-red-400 px-4 py-2 rounded-xl transition-colors"
              title="Download as professional PDF document"
              data-testid="export-pdf-btn"
            >
              <FileDown className="w-4 h-4" />
              Export PDF
            </button>
            
            <button
              onClick={handleExportCSV}
              disabled={!selectedProposal}
              className="flex items-center gap-2 bg-indigo-500/20 hover:bg-slate-400/30 disabled:opacity-50 text-indigo-400 px-4 py-2 rounded-xl transition-colors"
              title="Import this file into QuickBooks, Xero, or Wave"
              data-testid="export-csv-btn"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export CSV
            </button>
            
            <button
              onClick={() => setShowCSVGuide(true)}
              className="p-2 text-indigo-400 hover:text-indigo-400 hover:bg-slate-400/10 rounded-lg transition-colors"
              title="How to import CSV into accounting software"
              data-testid="csv-help-btn"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
            
            <button
              onClick={handleShare}
              disabled={!selectedProposal}
              className="flex items-center gap-2 bg-indigo-500/20 hover:bg-slate-400/30 disabled:opacity-50 text-indigo-400 px-4 py-2 rounded-xl transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share Link
            </button>
            
            <button
              onClick={() => setShowEmailComposer(true)}
              disabled={!mdBody}
              className="flex items-center gap-2 bg-green-500/20 hover:bg-green-500/30 disabled:opacity-50 text-green-400 px-4 py-2 rounded-xl transition-colors"
              data-testid="send-proposal-email-btn"
            >
              <Send className="w-4 h-4" />
              Send via Email
            </button>
            
            <button
              onClick={handleCopy}
              disabled={!mdBody}
              className="flex items-center gap-2 bg-slate-400 hover:bg-slate-600 disabled:opacity-50 text-indigo-500 px-4 py-2 rounded-xl transition-colors ml-auto"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
              Copy
            </button>
          </div>
          
          {shareUrl && (
            <div className="mt-3 p-3 bg-slate-900/50 rounded-lg flex items-center justify-between">
              <code className="text-sm text-indigo-400 truncate">{shareUrl}</code>
              <a
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-400 hover:text-indigo-400 ml-2"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-indigo-500 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-400" />
              Proposal Content
              {selectedProposal?.version && (
                <Badge variant="outline" className="ml-2 text-indigo-400">
                  v{selectedProposal.version}
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              {autoSaved && (
                <span className="text-xs bg-slate-400/50 text-indigo-400 px-3 py-1 rounded-full flex items-center gap-1.5 animate-fade-in">
                  <Save className="w-3 h-3" />
                  Draft saved
                </span>
              )}
              {hasDraft && (
                <button
                  onClick={() => {
                    clearDraft();
                    setMdBody("");
                    toast.success("Draft cleared");
                  }}
                  className="text-xs text-slate-400 hover:text-red-400 flex items-center gap-1 transition-colors"
                  title="Clear saved draft"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear draft
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Textarea
            value={mdBody}
            onChange={(e) => setMdBody(e.target.value)}
            placeholder="Generate a proposal or paste your content here..."
            className="min-h-[500px] font-mono text-sm bg-slate-900/50 border-slate-600 text-slate-600"
            data-testid="proposal-content"
          />
        </CardContent>
      </Card>

      {/* Previous Proposals */}
      {proposals.length > 1 && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 text-sm">Previous Versions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {proposals.slice(1).map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setSelectedProposal(p);
                    setMdBody(p.md_body || "");
                  }}
                  className="w-full text-left p-3 bg-slate-900/30 hover:bg-slate-900/50 rounded-lg transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{p.title}</span>
                    <span className="text-xs text-slate-400">v{p.version}</span>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Composer Modal */}
      <EmailComposer
        isOpen={showEmailComposer}
        onClose={() => setShowEmailComposer(false)}
        initialContent={getProposalEmailContent()}
        clients={clients}
      />

      {/* CSV Import Guide Modal */}
      {showCSVGuide && <CSVImportGuide onClose={() => setShowCSVGuide(false)} />}
    </div>
  );
}
